import { RenderAssetSourceCtx } from "datocms-plugin-sdk";
import { Button, TextInput, Spinner, useCtx } from "datocms-react-ui";
import classNames from "classnames";
import { useEffect, useState, FormEvent } from "react";

import { ImageShopPhotoData } from "./types";
import s from "./styles.module.css";

const AssetBrowser = () => {
  const ctx = useCtx<RenderAssetSourceCtx>();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [imageShopData, setImageShopData] = useState<ImageShopPhotoData[]>([]);
  const [page, setPage] = useState(9);

  // Check if the selected image has a subDocument + small jpg option
  const findSubdocument = (photo: ImageShopPhotoData) => {
    const hasSubDocument = photo.SubDocumentList.filter(
      (subDocument) =>
        subDocument.Code === "med" || subDocument.VersionName === "Medium"
    );
    if (hasSubDocument.length && hasSubDocument[0].SubDocumentID) {
      return `{
        "DocumentId": ${photo.DocumentID},
        "SubDocumentId": ${hasSubDocument[0].SubDocumentID},
        "Quality":'Subdocument',
        "DownloadAsAttachment": false
      }`;
    }

    return `{
      "DocumentId": ${photo.DocumentID},
      "Quality":'OriginalFile',
      "DownloadAsAttachment": false
    }`;
  };

  // pull image from Imageshop and upload selected image to Dato
  const handleImageShopSelect =
    // fetch image
    async (photo: ImageShopPhotoData) => {
      setLoading(true);
      const image = await fetch("https://api.imageshop.no/Download", {
        method: "POST",
        headers: {
          "Content-Type": "text/json",
          Accept: "application/json",
          token: `${process.env.REACT_APP_API_KEY}`,
        },
        body: findSubdocument(photo),
      });
      const json = await image.json();

      // upload image to Dato
      ctx.select({
        resource: {
          url: `${json.Url}`,
          filename: `${photo.FileName}`,
        },
        author: photo.AuthorName || "unknown",
        notes: photo.Description || undefined,
      });
      setLoading(false);
    };

  // call imageshop API
  const fetchData = async () => {
    setLoading(true);
    const response = await fetch("https://api.imageshop.no/Search2", {
      method: "POST",
      headers: {
        "Content-Type": "text/json",
        Accept: "application/json",
        token: `${process.env.REACT_APP_API_KEY}`,
      },
      body: `{"Querystring": "${query}","Pagesize": ${page} }`,
    });
    const json = await response.json();
    setImageShopData(json?.DocumentList || []);
    setLoading(false);
  };

  // call Imageshop API on page load
  useEffect(() => {
    fetchData().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // search for images
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPage(9);
    fetchData();
  };

  // check if all images have been displayed
  const availableImages = (imageObject: any) => {
    return imageObject?.NumberOfDocuments === imageObject?.length;
  };

  return (
    <div>
      <form className={s.search} onSubmit={(event) => handleSubmit(event)}>
        <div className={s.searchFirstRow}>
          <TextInput
            value={query}
            placeholder="Search imageShop..."
            onChange={(newValue) => setQuery(newValue)}
            className={s.search__input}
          />
          <Button type="submit" buttonSize="m" buttonType="primary">
            Search
          </Button>
        </div>
      </form>
      <div>
        <div className={classNames(s.container, s.grid)}>
          {loading && <Spinner size={50} placement="centered" />}
          {!loading && imageShopData
            ? imageShopData.map((image: ImageShopPhotoData) => {
              return (
                <div
                  onClick={handleImageShopSelect.bind(null, image)}
                  key={image.Code + image.DocumentID}
                >
                  <img src={image.LargeThumbUrl} alt={image.Description} />
                </div>
              );
            })
            : null}
        </div>
        <div className={s.footer}>
          <Button
            type="button"
            onClick={() => setPage(page + 9)}
            disabled={availableImages(imageShopData)}
            fullWidth
          >
            {availableImages(imageShopData)
              ? "All images loaded"
              : "Load more..."}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssetBrowser;
